/** Response contracts shared with the Django envelope (spec §100). */

export interface Quality {
  rows_considered: number;
  unresolved_employee_pct: number | null;
  unbridged_activity_pct: number | null;
  missing_completion_pct: number | null;
  suppressed_small_samples: number;
  notes: string[];
}

export interface Metadata {
  generated_at: string;
  /** When the underlying facts were last loaded — not when this JSON was built. */
  data_as_of: string | null;
  cache_age_seconds: number;
  source: string;
  scope: Record<string, unknown>;
  /** Definitions still awaiting business confirmation (spec §78). */
  assumptions: string[];
  elapsed_ms: number | null;
}

export interface AnalyticsEnvelope<S = Record<string, unknown>> {
  filters: Record<string, unknown>;
  summary: S;
  comparison: {
    basis?: string;
    range?: { start: string; end: string } | null;
    summary?: Record<string, number>;
    delta_pct?: Record<string, number | null>;
    trend?: Record<string, string>;
  };
  trend: Array<Record<string, unknown>>;
  breakdown: Array<Record<string, unknown>>;
  insights: Array<Record<string, unknown>>;
  quality: Quality;
  metadata: Metadata;
}

export interface ExecutiveSummary {
  activities: number;
  completed_activities: number;
  completed_pct: number | null;
  unique_clients: number;
  active_employees: number;
  positive_responses: number;
  leads: number;
  /** `New Leads` activities — the referrer handing over patient details. */
  information: number;
  admissions: number;
  conversions: number;
  /** Admissions divided by information received, NOT by rows in tabLeads. */
  conversion_pct: number | null;
  /** Share of lead records carrying an admission. A data-quality signal near
   *  99%, not a conversion rate — see the backend metric definition. */
  lead_record_completion_pct: number | null;
  revenue: number;
  revenue_per_conversion: number | null;
  commission_approved: number;
  commission_net: number;
  payout_ratio_pct: number | null;
  followups_due: number;
  followups_missed: number;
  followup_compliance_pct: number | null;
}

/** Spec §87 journey event. */
export type EntityType =
  | "client" | "activity" | "followup" | "response" | "commitment"
  | "lead" | "admission" | "billing" | "referral" | "conversion" | "revenue";

export interface JourneyEvent {
  id: string;
  entityType: EntityType;
  timestamp: string;
  title: string;
  description?: string;
  employeeName?: string;
  clientName?: string;
  leadId?: string;
  status?: string;
  value?: number | null;
  sourceTable?: string;
  sourceId?: string;
  /** "inferred" nodes were linked by window or by an unconfirmed definition. */
  linkConfidence?: "exact" | "inferred";
  /** Position on the Information -> Payout ladder, 1..8. */
  stage?: number;
  stageName?: string;
  stageMeaning?: string;
  metadata?: Record<string, unknown>;
}

export type Role = "ADMIN" | "MANAGEMENT" | "VP" | "MANAGER" | "EMPLOYEE";

/** Roles whose dashboards are limited to their own slice of the hierarchy, and
 *  which therefore cannot function without a resolved employee id. */
export const HIERARCHY_ROLES: Role[] = ["VP", "MANAGER", "EMPLOYEE"];

export const ROLE_LABEL: Record<Role, string> = {
  ADMIN: "Administrator",
  MANAGEMENT: "Management",
  VP: "Vice President",
  MANAGER: "Manager",
  EMPLOYEE: "Marketing employee",
};

export type ScopeKind =
  "VP" | "MANAGER" | "EMPLOYEE" | "CLIENT" | "AREA" | "BRANCH";

export interface ScopeGrant {
  id: number;
  kind: ScopeKind;
  value: string;
  reason?: string;
  expires_on?: string | null;
  granted_at?: string;
  granted_by_username?: string;
}

export interface ManagedUser {
  id: number;
  username: string;
  email: string;
  display_name: string;
  role: Role;
  employee_id: string;
  employee_id_namespace: string;
  person_key: string | null;
  /** The hierarchy's own name for this person; may differ from display_name. */
  resolved_name: string;
  is_active: boolean;
  may_see_patient_pii: boolean;
  may_see_financials: boolean;
  may_export: boolean;
  date_joined: string;
  last_login: string | null;
  scopes: ScopeGrant[];
  scope_count: number;
}

export interface UserListResponse {
  results: ManagedUser[];
  summary: {
    total: number;
    active: number;
    by_role: Record<string, number>;
    unlinked_hierarchy_accounts: number;
    pending_requests: number;
  };
}

export interface EmployeeIdCheck {
  ok: boolean;
  reason: string;
  namespace: string;
  name: string;
  masked: boolean;
  person_key?: string | null;
  designation?: string;
  method?: string;
  also_known_as?: string[];
}

export interface AccessRequestRow {
  id: number;
  username: string;
  email: string;
  full_name: string;
  employee_id: string;
  requested_role: Role;
  resolved_person_key: string;
  resolved_name: string;
  resolved_designation: string;
  resolution_note: string;
  status: "PENDING" | "APPROVED" | "REJECTED";
  requested_at: string;
  requested_ip: string | null;
  decided_at: string | null;
  decided_by_username: string;
  decision_note: string;
  /** Re-resolved live; `drifted` means the nightly rebuild moved the id. */
  current_resolution: (EmployeeIdCheck & { drifted: boolean }) | null;
}

export interface AccessAuditEntry {
  at: string;
  action: string;
  by: string;
  role: string;
  entity_type: string;
  entity_id: string;
  ip: string | null;
  detail: Record<string, unknown>;
}

export interface Insight {
  id: number;
  kind: "opportunity" | "risk" | "narrative";
  priority: "critical" | "high" | "medium" | "low";
  rule_code: string;
  title: string;
  body: string;
  recommended_action: string;
  entity_type: string;
  entity_id: string;
  entity_label: string;
  evidence: Array<{ label: string; value: unknown; unit?: string }>;
  impact_value: number | null;
  is_rule_based: boolean;
  as_of: string;
}

export interface QualityIssue {
  code: string;
  title: string;
  severity: "critical" | "high" | "medium" | "low";
  source_table: string;
  source_column: string;
  affected_count: number;
  total_count: number;
  affected_pct: number | null;
  recommended_action: string;
  blocks_features: string[];
  examples: string[];
  detected_at: string;
}

export interface CurrentUser {
  id: number;
  username: string;
  display_name: string;
  role: Role;
  person_key: string | null;
  employee_id: string | null;
  permissions: { patient_pii: boolean; financials: boolean; export: boolean };
  scope: Record<string, unknown>;
}
